/**
 * The employee ledger on Lønnsgrunnlag (V7.4, #281): each person's control status from the month's
 * checks, and the list-only search, status filter and page. None of this narrows the report or the
 * export: the totals, the checks and the files keep the period and scope chosen above the list.
 * Pure; tested in ledger.test.ts.
 */
import { fullName } from '@/ui/format';
import type { PayLine } from './lines';
import type { Check, CheckRecord } from './run';

export type RowState = 'avklares' | 'merknad' | 'kontrollert' | 'ingen';
export const ROW_STATES: RowState[] = ['avklares', 'merknad', 'kontrollert', 'ingen'];

/** The person a check record points at: the employee card or the registration it opens, else null. */
export const uidOf = (r: CheckRecord): number | null => {
  const m = r.to?.match(/^\/ansatte\/(\d+)|[?&]ansatt=(\d+)/);
  return m ? Number(m[1] ?? m[2]) : null;
};

/** The checks naming this person, in the order the page lists them. */
export const checksFor = (list: Check[], id: number) =>
  list.filter((c) => c.records?.some((r) => uidOf(r) === id));

/**
 * A person's control status: something stops the file or waits for a look (an unapproved or open day,
 * a blocker, a check that holds the send), only a note, nothing at all, or no hours to check.
 */
export function rowState(l: PayLine, mine: Check[], held: string[] = []): RowState {
  if (l.unapproved + l.open > 0 || mine.some((c) => c.severity === 'blocker' || held.includes(c.key)))
    return 'avklares';
  if (!l.parts.length && !l.approvedH) return 'ingen';
  return mine.length ? 'merknad' : 'kontrollert';
}

export interface ListFilter {
  q: string;
  state: RowState | null;
}

/** The rows the list shows: the name or the Ansattnr contains the search, and the status matches. */
export function filterRows<L extends PayLine>(lines: L[], f: ListFilter, stateOf: (l: L) => RowState): L[] {
  const q = f.q.trim().toLocaleLowerCase('nb');
  return lines.filter(
    (l) =>
      (!q ||
        fullName(l.u).toLocaleLowerCase('nb').includes(q) ||
        String(l.u.employee_id ?? '')
          .toLocaleLowerCase('nb')
          .includes(q)) &&
      (!f.state || stateOf(l) === f.state),
  );
}

export const PAGE_SIZE = 25;

/** One page of the rows; a page past the end shows the last one, so a narrower search never lands on nothing. */
export function pageOf<T>(rows: T[], asked: number, size = PAGE_SIZE) {
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const page = Math.min(Math.max(1, Number.isFinite(asked) ? Math.floor(asked) : 1), pages);
  const start = (page - 1) * size;
  return {
    rows: rows.slice(start, start + size),
    page,
    pages,
    first: rows.length ? start + 1 : 0,
    last: Math.min(start + size, rows.length),
  };
}
